<template>
  <Layout>
    <div class="agronomic-page">
      <div class="agronomic-header">
        <div>
          <div class="agronomic-eyebrow">Administração</div>
          <h1>Importação Agronômica</h1>
          <p>Gerencie a fila de extração de bulas, geração de planilhas e importação de recomendações.</p>
        </div>
        <div class="agronomic-header-actions">
          <button class="btn btn-light" :disabled="isLoading" @click.prevent="refreshAll(true)">
            <i class="uil uil-sync" :class="{ spinning: isLoading }"></i>
            Atualizar
          </button>
          <button class="btn btn-primary" :disabled="enqueueLoading || !selectedProductIds.length" @click.prevent="enqueueSelectedProducts">
            <i class="uil uil-plus-circle"></i>
            Adicionar à fila
          </button>
        </div>
      </div>

      <div class="status-grid">
        <div v-for="item in statusIndicators" :key="item.status" class="status-card">
          <span :class="['job-badge', statusBadgeClass(item.status)]">{{ item.label }}</span>
          <strong>{{ item.count }}</strong>
        </div>
      </div>

      <div class="agronomic-grid">
        <section class="ops-panel">
          <div class="panel-title">Produtos</div>
          <div class="panel-subtitle">Selecione produtos da mesma base usada em Relações ou cole IDs para testes em lote.</div>

          <b-form-group label="Buscar produtos">
            <v-select
              class="style-select product-select"
              multiple
              label="nome"
              :options="products"
              :reduce="(item) => item.id_produto"
              v-model="selectedProductIds"
              :filterable="true"
              :close-on-select="false"
              placeholder="Selecione um ou mais produtos"
            >
              <template #option="item">
                <div class="product-option">
                  <div class="product-option-header">
                    <strong>#{{ item.id_produto }} - {{ item.nome }}</strong>
                    <span v-if="item.job_status" :class="['job-badge', statusBadgeClass(item.job_status)]">{{ item.job_status }}</span>
                  </div>
                  <span>MAPA {{ item.mapa || '-' }} | Usos {{ item.total_usos || '-' }} | Relações {{ item.total_itens_produto || '-' }}</span>
                  <span v-if="item.ultimo_erro" class="inline-error">{{ truncateText(item.ultimo_erro, 110) }}</span>
                  <button
                    v-if="item.ultimo_erro || item.id_job"
                    class="btn btn-link btn-sm product-detail-link"
                    @mousedown.stop.prevent="openProductDetails(item)"
                  >
                    Detalhes
                  </button>
                </div>
              </template>
              <template #selected-option="item">
                <span>#{{ item.id_produto }} - {{ item.nome }} <strong v-if="item.job_status === 'ERRO'" class="selected-error">ERRO</strong></span>
              </template>
            </v-select>
          </b-form-group>

          <b-form-group label="Colar IDs">
            <b-form-textarea
              v-model="pastedProductIds"
              rows="4"
              placeholder="101, 102, 103 ou um ID por linha"
              @blur="applyPastedProductIds"
            ></b-form-textarea>
          </b-form-group>

          <div class="selection-summary">
            <span>{{ selectedProductIds.length }} produto(s) selecionado(s)</span>
            <button class="btn btn-link btn-sm" :disabled="!selectedProductIds.length" @click.prevent="clearSelection">
              Limpar seleção
            </button>
          </div>

          <div v-if="selectedProductsWithJobInfo.length" class="selected-product-alerts">
            <div v-for="product in selectedProductsWithJobInfo" :key="product.id_produto" class="selected-product-alert">
              <div>
                <strong>{{ product.nome }}</strong>
                <span v-if="product.ultimo_erro">{{ truncateText(product.ultimo_erro, 120) }}</span>
              </div>
              <button class="btn btn-link btn-sm" @click.prevent="openProductDetails(product)">Detalhes</button>
            </div>
          </div>
        </section>

        <section class="ops-panel">
          <div class="panel-title">Configuração da importação</div>
          <div class="panel-subtitle">Defina o comportamento antes de enfileirar os produtos.</div>

          <div class="switch-row">
            <b-form-checkbox v-model="enqueueForm.confirmar" switch>
              Confirmar importação
            </b-form-checkbox>
            <b-form-checkbox v-model="enqueueForm.inativar_ausentes" switch>
              Inativar ausentes
            </b-form-checkbox>
          </div>

          <b-alert v-if="enqueueForm.inativar_ausentes" show variant="warning" class="soft-alert">
            Relações antigas que não vierem na nova bula podem ser inativadas.
          </b-alert>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Prioridade">
                <b-form-input v-model.number="enqueueForm.prioridade" type="number"></b-form-input>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Agendar para">
                <b-form-input v-model="enqueueForm.agendado_para" type="datetime-local"></b-form-input>
              </b-form-group>
            </div>
          </div>

          <b-form-group label="Culturas específicas">
            <v-select
              class="style-select"
              multiple
              taggable
              :options="cultureTags"
              v-model="enqueueForm.culturas"
              placeholder="Digite e pressione Enter"
            ></v-select>
          </b-form-group>

          <div v-if="lastEnqueueSummary" class="result-box">
            <strong>Último enfileiramento</strong>
            <span>{{ formatApiSummary(lastEnqueueSummary) }}</span>
          </div>
        </section>

        <section class="ops-panel run-panel">
          <div>
            <div class="panel-title">Processamento manual</div>
            <div class="panel-subtitle">Execute um lote pequeno de jobs pendentes quando necessário.</div>
          </div>
          <b-form-group label="Limite">
            <b-form-input v-model.number="runLimit" type="number" min="1" max="10"></b-form-input>
          </b-form-group>
          <button class="btn btn-warning btn-block" :disabled="runLoading" @click.prevent="runPendingJobs">
            <i class="uil uil-play"></i>
            Processar próximos
          </button>
          <div v-if="lastRunSummary" class="result-box">
            <strong>Último processamento</strong>
            <span>{{ formatRunSummary(lastRunSummary) }}</span>
            <div v-if="lastRunResults.length" class="run-results">
              <div v-for="result in lastRunResults" :key="resultKey(result)" class="run-result-item">
                <span>{{ result.id_produto || result.produto_id || result.produto || '-' }}</span>
                <strong>{{ result.status || result.resultado || result.sucesso || '-' }}</strong>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section class="queue-panel">
        <div class="queue-toolbar">
          <div>
            <div class="panel-title">Fila de importação</div>
            <div class="panel-subtitle">Acompanhe jobs pendentes, processando, concluídos ou com erro.</div>
          </div>
          <div class="queue-filters">
            <b-form-select v-model="jobFilters.status" :options="statusOptions"></b-form-select>
            <b-form-input v-model.number="jobFilters.limit" type="number" min="1" max="200"></b-form-input>
            <button class="btn btn-light" :disabled="jobsLoading" @click.prevent="loadJobs">
              Atualizar lista
            </button>
          </div>
        </div>

        <div v-if="jobsLoading" class="queue-state">Carregando fila...</div>
        <div v-else class="queue-table-wrap">
          <table class="table table-sm table-bordered queue-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th>Status</th>
                <th>Prioridade</th>
                <th>Tentativas</th>
                <th>Agendado para</th>
                <th>Iniciado em</th>
                <th>Finalizado em</th>
                <th>Atualizado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in jobs" :key="getJobId(job)" :class="{ 'queue-row-error': hasJobError(job) }">
                <td>{{ job.id_produto || job.produto_id || '-' }} - {{ resolveJobProductName(job) }}</td>
                <td>
                  <span :class="['job-badge', statusBadgeClass(getJobStatus(job))]">{{ getJobStatus(job) || '-' }}</span>
                  <span v-if="hasJobError(job)" class="error-dot" title="Job com erro"></span>
                </td>
                <td>{{ job.prioridade !== undefined ? job.prioridade : '-' }}</td>
                <td>{{ formatAttempts(job) }}</td>
                <td>{{ formatDateTimeValue(job.agendado_para || job.agendadoPara) }}</td>
                <td>{{ formatDateTimeValue(job.iniciado_em || job.iniciadoEm) }}</td>
                <td>{{ formatDateTimeValue(job.finalizado_em || job.finalizadoEm) }}</td>
                <td>{{ formatDateTimeValue(job.updated_at || job.updatedAt) }}</td>
                <td>
                  <div class="queue-actions">
                    <button
                      class="btn btn-outline-primary btn-sm"
                      :disabled="enqueueLoading || isJobReimporting(job) || !getJobProductId(job)"
                      @click.prevent="reimportJob(job)"
                    >
                      <i class="uil uil-redo"></i>
                      Reimportar
                    </button>
                    <button class="btn btn-light btn-sm" @click.prevent="openJobDetails(job)">Detalhes</button>
                  </div>
                </td>
              </tr>
              <tr v-if="!jobs.length">
                <td colspan="14" class="queue-empty">Nenhum job encontrado.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <b-modal v-model="showDetailModal" size="xl" :title="detailModalTitle" hide-footer>
        <div v-if="detailRecord" class="detail-modal-content">
          <div class="detail-grid">
            <div v-for="item in detailSummaryItems" :key="item.label" class="detail-item">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
          </div>

          <div v-if="detailConfigItems.length" class="detail-section">
            <div class="detail-section-title">Configuração</div>
            <div class="detail-grid">
              <div v-for="item in detailConfigItems" :key="item.label" class="detail-item">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <div class="detail-section-title">Erro completo</div>
            <pre class="json-block error-json">{{ formatDetailValue(getDetailError(detailRecord)) }}</pre>
          </div>

          <div v-if="detailResultSummaryItems.length" class="detail-section">
            <div class="detail-section-title">Resultado</div>
            <div class="detail-grid">
              <div v-for="item in detailResultSummaryItems" :key="item.label" class="detail-item">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <div class="detail-section-title">JSON bruto do resultado</div>
            <pre class="json-block">{{ formatDetailValue(detailRecord.ultimo_resultado || detailRecord.ultimoResultado || detailRecord.resultado || (detailRecord.ultimo_log && detailRecord.ultimo_log.resultado)) }}</pre>
          </div>

          <div class="detail-section">
            <div class="detail-section-title">Histórico de execuções</div>
            <div v-if="jobLogsLoading" class="queue-state compact-state">Carregando histórico...</div>
            <div v-else-if="jobLogs.length" class="logs-list">
              <div
                v-for="log in jobLogs"
                :key="log.id"
                :class="['log-item', { 'log-item-error': hasLogError(log), 'log-item-success': String(log.status || '').toUpperCase() === 'SUCESSO' }]"
              >
                <div class="log-main">
                  <span :class="['job-badge', statusBadgeClass(log.status)]">{{ log.status || '-' }}</span>
                  <span>{{ formatDateTimeValue(log.iniciado_em) }}</span>
                  <span>{{ formatDateTimeValue(log.finalizado_em) }}</span>
                  <span>{{ formatDuration(log.duracao_ms) }}</span>
                  <span>Registros {{ formatMetric(log.registros_gerados) }}</span>
                  <span>Culturas {{ formatMetric(log.culturas) }}</span>
                  <span>Inclusões {{ formatMetric(log.inclusoes) }}</span>
                  <span>Alterações {{ formatMetric(log.alteracoes) }}</span>
                  <span>Sem alteração {{ formatMetric(log.sem_alteracao) }}</span>
                  <span>Inativações {{ formatMetric(log.inativacoes) }}</span>
                  <span>Falhas {{ formatMetric(log.falhas) }}</span>
                  <button class="btn btn-link btn-sm" @click.prevent="toggleLogDetails(log)">
                    {{ isLogExpanded(log) ? 'Ocultar' : 'Expandir' }}
                  </button>
                </div>
                <div v-if="isLogExpanded(log)" class="log-details">
                  <div>
                    <div class="detail-section-title">Erro</div>
                    <pre class="json-block error-json">{{ formatDetailValue(log.erro) }}</pre>
                  </div>
                  <div>
                    <div class="detail-section-title">Resultado</div>
                    <pre class="json-block">{{ formatDetailValue(log.resultado) }}</pre>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="history-placeholder">
              Nenhum histórico de execução encontrado para este job.
            </div>
          </div>

          <div v-if="jobLogsUnavailable" class="history-placeholder">
            Histórico completo ainda não disponível pela API.
          </div>
        </div>
      </b-modal>
    </div>
  </Layout>
</template>

<script>
import Layout from '@layouts/main'
import AgronomicImportService from '@/src/services/agronomic-import'

export default {
  name: 'AgronomicImport',
  components: { Layout },
  data() {
    return {
      products: [],
      jobs: [],
      summary: null,
      jobLogs: [],
      expandedLogIds: [],
      selectedProductIds: [],
      pastedProductIds: '',
      detailKind: '',
      detailRecord: null,
      showDetailModal: false,
      cultureTags: [],
      enqueueForm: {
        confirmar: true,
        inativar_ausentes: false,
        prioridade: 0,
        culturas: [],
        agendado_para: '',
      },
      jobFilters: {
        status: '',
        limit: 100,
      },
      runLimit: 10,
      productsLoading: false,
      jobsLoading: false,
      summaryLoading: false,
      jobLogsLoading: false,
      jobLogsUnavailable: false,
      enqueueLoading: false,
      reimportingJobIds: [],
      runLoading: false,
      lastEnqueueSummary: null,
      lastRunSummary: null,
      statusOptions: [
        { value: '', text: 'Todos os status' },
        { value: 'PENDENTE', text: 'Pendente' },
        { value: 'PROCESSANDO', text: 'Processando' },
        { value: 'SUCESSO', text: 'Sucesso' },
        { value: 'ERRO', text: 'Erro' },
        { value: 'IGNORADO', text: 'Ignorado' },
      ],
    }
  },
  computed: {
    isLoading() {
      return this.productsLoading || this.jobsLoading || this.summaryLoading || this.jobLogsLoading || this.enqueueLoading || this.runLoading
    },
    statusIndicators() {
      const summary = this.summary || {}
      const fallbackCount = (status) => this.jobs.filter((job) => this.getJobStatus(job) === status).length

      return [
        { status: 'TOTAL', label: 'Total', count: this.firstPresent([summary.total, this.jobs.length]) },
        { status: 'PENDENTE', label: 'Pendente', count: this.firstPresent([summary.pendente, fallbackCount('PENDENTE')]) },
        { status: 'PROCESSANDO', label: 'Processando', count: this.firstPresent([summary.processando, fallbackCount('PROCESSANDO')]) },
        { status: 'SUCESSO', label: 'Sucesso', count: this.firstPresent([summary.sucesso, fallbackCount('SUCESSO')]) },
        { status: 'ERRO', label: 'Erro', count: this.firstPresent([summary.erro, fallbackCount('ERRO')]) },
        { status: 'IGNORADO', label: 'Ignorado', count: this.firstPresent([summary.ignorado, fallbackCount('IGNORADO')]) },
      ]
    },
    lastRunResults() {
      if (!this.lastRunSummary || typeof this.lastRunSummary !== 'object') return []
      return this.extractRows({
        dados:
          this.lastRunSummary.resultados ||
          this.lastRunSummary.results ||
          this.lastRunSummary.items ||
          this.lastRunSummary.jobs ||
          [],
      })
    },
    selectedProductsWithJobInfo() {
      return this.products.filter((product) =>
        this.selectedProductIds.map((id) => String(id)).includes(String(product.id_produto)) &&
        (product.ultimo_erro || product.id_job || product.job_status)
      )
    },
    detailModalTitle() {
      if (this.detailKind === 'product') return 'Detalhes do produto urgente'
      return 'Detalhes do job'
    },
    detailSummaryItems() {
      if (!this.detailRecord) return []

      if (this.detailKind === 'product') {
        return [
          { label: 'ID do produto', value: this.detailRecord.id_produto || '-' },
          { label: 'Produto', value: this.detailRecord.nome || this.detailRecord.produto || '-' },
          { label: 'MAPA', value: this.detailRecord.mapa || this.detailRecord.mapa_produto || '-' },
          { label: 'ID do job', value: this.detailRecord.id_job || '-' },
          { label: 'Status do job', value: this.detailRecord.job_status || '-' },
          { label: 'Prioridade', value: this.detailRecord.job_prioridade !== undefined && this.detailRecord.job_prioridade !== null ? this.detailRecord.job_prioridade : '-' },
          { label: 'Tentativas', value: this.detailRecord.job_tentativas !== undefined && this.detailRecord.job_tentativas !== null ? this.detailRecord.job_tentativas : '-' },
          { label: 'Total de usos', value: this.detailRecord.total_usos || '-' },
          { label: 'Total de relações', value: this.detailRecord.total_itens_produto || '-' },
          { label: 'Total de culturas', value: this.detailRecord.total_culturas || '-' },
          { label: 'Total de alvos', value: this.detailRecord.total_alvos || '-' },
          { label: 'Última receita', value: this.formatDateTimeValue(this.detailRecord.ultima_receita_em) },
          { label: 'Última atualização agronômica', value: this.formatDateTimeValue(this.detailRecord.ultima_atualizacao_agronomica_em) },
        ]
      }

      return [
        { label: 'ID do job', value: this.getJobId(this.detailRecord) },
        { label: 'ID do produto', value: this.detailRecord.id_produto || this.detailRecord.produto_id || '-' },
        { label: 'Produto', value: this.resolveJobProductName(this.detailRecord) },
        { label: 'MAPA', value: this.detailRecord.produto_mapa || '-' },
        { label: 'Bula product ID', value: this.detailRecord.bula_product_id || '-' },
        { label: 'Status', value: this.getJobStatus(this.detailRecord) || '-' },
        { label: 'Prioridade', value: this.detailRecord.prioridade !== undefined && this.detailRecord.prioridade !== null ? this.detailRecord.prioridade : '-' },
        { label: 'Tentativas', value: this.formatAttempts(this.detailRecord) },
        { label: 'Pode reprocessar', value: this.formatBoolean(this.resolveField(this.detailRecord.andamento, ['pode_reprocessar', 'podeReprocessar'])) },
        { label: 'Em execução', value: this.formatBoolean(this.resolveField(this.detailRecord.andamento, ['em_execucao', 'emExecucao'])) },
        { label: 'Finalizado', value: this.formatBoolean(this.resolveField(this.detailRecord.andamento, ['finalizado'])) },
        { label: 'Confirmar importação', value: this.formatBoolean(this.detailRecord.confirmar) },
        { label: 'Inativar ausentes', value: this.formatBoolean(this.resolveField(this.detailRecord, ['inativar_ausentes', 'inativarAusentes'])) },
        { label: 'Culturas filtradas', value: this.formatCultures(this.detailRecord.culturas) },
        { label: 'Agendado para', value: this.formatDateTimeValue(this.detailRecord.agendado_para || this.detailRecord.agendadoPara) },
        { label: 'Iniciado em', value: this.formatDateTimeValue(this.detailRecord.iniciado_em || this.detailRecord.iniciadoEm) },
        { label: 'Finalizado em', value: this.formatDateTimeValue(this.detailRecord.finalizado_em || this.detailRecord.finalizadoEm) },
        { label: 'Última atualização agronômica', value: this.formatDateTimeValue(this.detailRecord.ultima_atualizacao_em || this.detailRecord.atualizado_em) },
        { label: 'Criado em', value: this.formatDateTimeValue(this.detailRecord.created_at) },
        { label: 'Atualizado em', value: this.formatDateTimeValue(this.detailRecord.updated_at || this.detailRecord.updatedAt) },
      ]
    },
    detailConfigItems() {
      if (!this.detailRecord || this.detailKind !== 'product') return []

      return [
        { label: 'Culturas', value: this.detailRecord.culturas || '-' },
        { label: 'Alvos', value: this.detailRecord.alvos || '-' },
      ]
    },
    parsedDetailResult() {
      if (!this.detailRecord) return null
      return this.normalizeResultPayload(this.detailRecord.ultimo_resultado || this.detailRecord.ultimoResultado || this.detailRecord.resultado || (this.detailRecord.ultimo_log && this.detailRecord.ultimo_log.resultado))
    },
    detailResultSummaryItems() {
      const result = this.parsedDetailResult
      const metrics = this.detailRecord && this.detailRecord.metricas ? this.detailRecord.metricas : {}
      if ((!result || typeof result !== 'object') && !Object.keys(metrics).length) return []
      const safeResult = result && typeof result === 'object' ? result : {}
      const importResult = safeResult.importResult || safeResult.import_result || safeResult.importacao || safeResult.resultado || {}

      return [
        { label: 'Produto', value: safeResult.produto || safeResult.nomeProduto || safeResult.nome_produto || this.detailRecord.produto_nome || '-' },
        { label: 'MAPA', value: safeResult.mapa || safeResult.mapa_produto || this.detailRecord.produto_mapa || '-' },
        { label: 'Culturas', value: safeResult.culturas || importResult.culturas || metrics.culturas || '-' },
        { label: 'Registros', value: this.firstPresent([this.resolveField(safeResult, ['registros', 'registros_gerados']), this.resolveField(importResult, ['registros', 'registros_gerados']), metrics.registros_gerados]) },
        { label: 'Inclusões', value: this.firstPresent([this.resolveField(importResult, ['inclusoes', 'inclusões']), this.resolveField(safeResult, ['inclusoes', 'inclusões']), metrics.inclusoes]) },
        { label: 'Alterações', value: this.firstPresent([this.resolveField(importResult, ['alteracoes', 'alterações']), this.resolveField(safeResult, ['alteracoes', 'alterações']), metrics.alteracoes]) },
        { label: 'Sem alteração', value: this.firstPresent([this.resolveField(importResult, ['sem_alteracao', 'semAlteracao']), this.resolveField(safeResult, ['sem_alteracao', 'semAlteracao']), metrics.sem_alteracao]) },
        { label: 'Inativações', value: this.firstPresent([this.resolveField(importResult, ['inativacoes', 'inativações']), this.resolveField(safeResult, ['inativacoes', 'inativações']), metrics.inativacoes]) },
        { label: 'Falhas', value: this.firstPresent([this.resolveField(importResult, ['falhas']), this.resolveField(safeResult, ['falhas']), metrics.falhas]) },
        { label: 'Erros', value: this.firstPresent([this.resolveField(importResult, ['erros']), this.resolveField(safeResult, ['erros'])]) },
      ].map((item) => ({
        ...item,
        value: item.value !== undefined && item.value !== null && item.value !== '' ? item.value : '-',
      }))
    },
  },
  mounted() {
    this.refreshAll()
  },
  methods: {
    getToken() {
      return this.$store.state.auth.currentUser.token
    },
    async refreshAll() {
      await Promise.all([this.loadProducts(), this.loadJobs(), this.loadSummary()])
    },
    async loadProducts() {
      this.productsLoading = true
      try {
        const response = await AgronomicImportService.listUrgentProducts(this.getToken(), 100)
        const data = response && response.data ? response.data : response
        this.products = this.extractRows(data).map(this.normalizeProductOption)
      } catch (error) {
        this.showApiError(error, 'Não foi possível carregar produtos.')
      } finally {
        this.productsLoading = false
      }
    },
    async loadJobs() {
      this.jobsLoading = true
      try {
        const params = {
          limit: this.normalizeLimit(this.jobFilters.limit, 100, 200),
        }
        if (this.jobFilters.status) params.status = this.jobFilters.status

        const response = await AgronomicImportService.listJobs(this.getToken(), params)
        const data = response && response.data ? response.data : response
        this.jobs = this.extractRows(data)
      } catch (error) {
        this.showApiError(error, 'Não foi possível carregar a fila.')
      } finally {
        this.jobsLoading = false
      }
    },
    async loadSummary() {
      this.summaryLoading = true
      try {
        const response = await AgronomicImportService.getSummary(this.getToken())
        this.summary = response && response.data ? response.data : response
      } catch (error) {
        this.summary = null
        this.showApiError(error, 'Não foi possível carregar o resumo da fila.')
      } finally {
        this.summaryLoading = false
      }
    },
    applyPastedProductIds() {
      const ids = String(this.pastedProductIds || '')
        .split(/[,\n\r;\s]+/)
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value) && value > 0)

      this.selectedProductIds = Array.from(new Set([...this.selectedProductIds, ...ids]))
    },
    clearSelection() {
      this.selectedProductIds = []
      this.pastedProductIds = ''
    },
    buildEnqueuePayload(productIds = this.selectedProductIds) {
      return {
        produtos: productIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id)),
        confirmar: Boolean(this.enqueueForm.confirmar),
        inativar_ausentes: Boolean(this.enqueueForm.inativar_ausentes),
        prioridade: Number(this.enqueueForm.prioridade || 0),
        culturas: (this.enqueueForm.culturas || []).map((item) => String(item).trim()).filter(Boolean),
        agendado_para: this.enqueueForm.agendado_para
          ? new Date(this.enqueueForm.agendado_para).toISOString()
          : null,
      }
    },
    async enqueueSelectedProducts() {
      const payload = this.buildEnqueuePayload()
      if (!payload.produtos.length) {
        this.$bvToast.toast('Selecione ao menos um produto.', { variant: 'warning', solid: false, noCloseButton: true, toaster: 'b-toaster-top-center' })
        return
      }

      this.enqueueLoading = true
      try {
        const response = await AgronomicImportService.enqueueJobs(this.getToken(), payload)
        this.lastEnqueueSummary = response && response.data ? response.data : response
        this.clearSelection()
        await Promise.all([this.loadJobs(), this.loadSummary(), this.loadProducts()])
        this.$bvToast.toast('Produtos adicionados à fila.', { variant: 'success', solid: false, noCloseButton: true, toaster: 'b-toaster-top-center' })
      } catch (error) {
        this.showApiError(error, 'Não foi possível adicionar produtos à fila.')
      } finally {
        this.enqueueLoading = false
      }
    },
    async reimportJob(job) {
      const productId = this.getJobProductId(job)
      if (!productId) {
        this.$bvToast.toast('Não foi possível identificar o produto deste job.', { variant: 'warning', solid: false, noCloseButton: true, toaster: 'b-toaster-top-center' })
        return
      }

      const productName = this.resolveJobProductName(job)
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Reimportar o produto ${productName !== '-' ? productName : `#${productId}`}?`,
        {
          title: 'Confirmar reimportação',
          okTitle: 'Reimportar',
          cancelTitle: 'Cancelar',
          okVariant: 'primary',
        }
      )
      if (!confirmed) return

      const jobId = this.getJobId(job)
      this.reimportingJobIds = [...this.reimportingJobIds, String(jobId)]
      try {
        const response = await AgronomicImportService.enqueueJobs(this.getToken(), this.buildEnqueuePayload([productId]))
        this.lastEnqueueSummary = response && response.data ? response.data : response
        await Promise.all([this.loadJobs(), this.loadSummary(), this.loadProducts()])
        this.$bvToast.toast('Produto adicionado novamente à fila.', { variant: 'success', solid: false, noCloseButton: true, toaster: 'b-toaster-top-center' })
      } catch (error) {
        this.showApiError(error, 'Não foi possível reimportar o produto.')
      } finally {
        this.reimportingJobIds = this.reimportingJobIds.filter((id) => id !== String(jobId))
      }
    },
    async runPendingJobs() {
      const limit = this.normalizeLimit(this.runLimit, 10, 10)
      this.runLimit = limit

      if (limit > 1) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Processar os próximos ${limit} jobs pendentes agora?`,
          {
            title: 'Confirmar processamento manual',
            okTitle: 'Processar',
            cancelTitle: 'Cancelar',
            okVariant: 'warning',
          }
        )
        if (!confirmed) return
      }

      this.runLoading = true
      try {
        const response = await AgronomicImportService.runJobs(this.getToken(), limit)
        this.lastRunSummary = response && response.data ? response.data : response
        await Promise.all([this.loadJobs(), this.loadSummary(), this.loadProducts()])
        this.$bvToast.toast('Processamento manual concluído.', { variant: 'success', solid: false, noCloseButton: true, toaster: 'b-toaster-top-center' })
      } catch (error) {
        this.showApiError(error, 'Não foi possível processar a fila.')
      } finally {
        this.runLoading = false
      }
    },
    normalizeLimit(value, fallback, max) {
      const parsed = Number(value)
      if (Number.isNaN(parsed) || parsed < 1) return fallback
      return Math.min(Math.floor(parsed), max)
    },
    extractRows(data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.dados)) return data.dados
      if (data && Array.isArray(data.data)) return data.data
      if (data && Array.isArray(data.jobs)) return data.jobs
      if (data && Array.isArray(data.items)) return data.items
      return []
    },
    normalizeProductOption(product) {
      return {
        ...product,
        nome: product.nome || product.produto || product.nome_produto || '',
        mapa: product.mapa || product.mapa_produto || '',
      }
    },
    getJobId(job) {
      return job.id || job.id_job || job.job_id || '-'
    },
    getJobProductId(job) {
      return job.id_produto || job.produto_id || job.product_id || ''
    },
    isJobReimporting(job) {
      return this.reimportingJobIds.includes(String(this.getJobId(job)))
    },
    getJobError(job) {
      return job.ultimo_erro || job.ultimoErro || job.error || ''
    },
    hasJobError(job) {
      return this.getJobStatus(job) === 'ERRO' || Boolean(this.getJobError(job))
    },
    async openJobDetails(job) {
      this.detailKind = 'job'
      this.detailRecord = job
      this.showDetailModal = true
      await this.loadJobLogs(this.getJobId(job))
    },
    async openProductDetails(product) {
      this.detailKind = 'product'
      this.detailRecord = product
      this.showDetailModal = true
      await this.loadJobLogs(product.id_job)
    },
    async loadJobLogs(jobId) {
      this.jobLogs = []
      this.expandedLogIds = []
      this.jobLogsUnavailable = false
      if (!jobId || jobId === '-') return

      this.jobLogsLoading = true
      try {
        const response = await AgronomicImportService.listJobLogs(this.getToken(), jobId, 50)
        const data = response && response.data ? response.data : response
        this.jobLogs = this.extractRows(data)
      } catch (error) {
        this.jobLogsUnavailable = true
      } finally {
        this.jobLogsLoading = false
      }
    },
    getDetailError(record) {
      if (!record) return ''
      return record.ultimo_erro || record.ultimoErro || record.error || record.erros || (record.ultimo_log && record.ultimo_log.erro) || ''
    },
    truncateText(value, maxLength = 120) {
      const text = this.formatInlineValue(value)
      if (!text) return '-'
      if (text.length <= maxLength) return text
      return `${text.slice(0, maxLength - 3)}...`
    },
    formatInlineValue(value) {
      if (value === undefined || value === null || value === '') return ''
      if (typeof value === 'string') return value
      try {
        return JSON.stringify(value)
      } catch (error) {
        return String(value)
      }
    },
    formatDetailValue(value) {
      if (value === undefined || value === null || value === '') return '-'
      if (typeof value === 'string') {
        const parsed = this.parseJson(value)
        return parsed ? JSON.stringify(parsed, null, 2) : value
      }
      try {
        return JSON.stringify(value, null, 2)
      } catch (error) {
        return String(value)
      }
    },
    parseJson(value) {
      try {
        return JSON.parse(value)
      } catch (error) {
        return null
      }
    },
    normalizeResultPayload(value) {
      if (!value) return null
      if (typeof value === 'string') return this.parseJson(value)
      return value
    },
    formatAttempts(job) {
      const andamento = job.andamento || {}
      const attempts = this.firstPresent([andamento.tentativas, job.tentativas])
      const maxAttempts = this.firstPresent([andamento.max_tentativas, andamento.maxTentativas, job.max_tentativas, job.maxTentativas])
      return `${attempts !== undefined ? attempts : '-'} / ${maxAttempts !== undefined ? maxAttempts : '-'}`
    },
    getJobStatus(job) {
      return String(this.resolveField(job.andamento, ['status']) || job.status || '').toUpperCase()
    },
    hasLogError(log) {
      return String(log.status || '').toUpperCase() === 'ERRO' || Boolean(log.erro)
    },
    formatMetric(value) {
      return value !== undefined && value !== null && value !== '' ? value : '-'
    },
    formatDuration(value) {
      if (value === undefined || value === null || value === '') return '-'
      const duration = Number(value)
      if (Number.isNaN(duration)) return value
      if (duration < 1000) return `${duration}ms`
      return `${(duration / 1000).toFixed(1)}s`
    },
    toggleLogDetails(log) {
      const id = String(log.id)
      if (this.expandedLogIds.includes(id)) {
        this.expandedLogIds = this.expandedLogIds.filter((item) => item !== id)
        return
      }
      this.expandedLogIds = [...this.expandedLogIds, id]
    },
    isLogExpanded(log) {
      return this.expandedLogIds.includes(String(log.id))
    },
    formatCultures(value) {
      if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
      return value || '-'
    },
    formatBoolean(value) {
      if (value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true') return 'Sim'
      if (value === false || value === 0 || value === '0' || String(value).toLowerCase() === 'false') return 'Não'
      return '-'
    },
    resolveField(record, keys) {
      if (!record) return undefined
      for (let index = 0; index < keys.length; index += 1) {
        const key = keys[index]
        if (record[key] !== undefined && record[key] !== null) return record[key]
      }
      return undefined
    },
    firstPresent(values) {
      return values.find((value) => value !== undefined && value !== null && value !== '')
    },
    productStatusLabel(product) {
      if (this.isProductActive(product)) return 'Ativo'
      if (this.isProductInactive(product)) return 'Inativo'
      return 'Status não informado'
    },
    isProductActive(product) {
      const value = product ? product.ativo : null
      return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true'
    },
    isProductInactive(product) {
      const value = product ? product.ativo : null
      return value === false || value === 0 || value === '0' || String(value).toLowerCase() === 'false'
    },
    resolveJobProductName(job) {
      const product =
        job.produto ||
        job.product ||
        (job.payload && job.payload.produto) ||
        (job.payload && job.payload.product) ||
        {}
      if (typeof product === 'string') return product
      return product.nome || product.name || job.produto_nome || job.nome_produto || '-'
    },
    statusBadgeClass(status) {
      const normalized = String(status || '').toUpperCase()
      if (normalized === 'TOTAL') return 'job-badge-total'
      if (normalized === 'PROCESSANDO') return 'job-badge-processing'
      if (normalized === 'SUCESSO') return 'job-badge-success'
      if (normalized === 'ERRO') return 'job-badge-error'
      if (normalized === 'IGNORADO') return 'job-badge-muted'
      return 'job-badge-pending'
    },
    formatDateTimeValue(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('pt-BR')
    },
    formatApiSummary(payload) {
      if (!payload || typeof payload !== 'object') return '-'
      const created = payload.criados || payload.created || payload.inseridos || 0
      const updated = payload.atualizados || payload.updated || 0
      const kept = payload.mantidos || payload.kept || payload.ignorados || 0
      return `Criados: ${created} | Atualizados: ${updated} | Mantidos/Ignorados: ${kept}`
    },
    formatRunSummary(payload) {
      if (!payload || typeof payload !== 'object') return '-'
      const processed = payload.total_processado || payload.processados || payload.total || 0
      const success = payload.sucesso || payload.success || payload.total_sucesso || 0
      const error = payload.erro || payload.errors || payload.total_erro || 0
      return `Processados: ${processed} | Sucesso: ${success} | Erro: ${error}`
    },
    resultKey(result) {
      return [
        result.id || result.id_job || result.job_id || '',
        result.id_produto || result.produto_id || result.produto || '',
        result.status || result.resultado || '',
      ].join('-')
    },
    showApiError(error, fallback) {
      const data = error && error.response && error.response.data ? error.response.data : {}
      const message = data.message || data.mensagem || data.msg || data.error || fallback
      this.$bvToast.toast(Array.isArray(message) ? message.join(', ') : message, {
        variant: 'danger',
        solid: false,
        noCloseButton: true,
        toaster: 'b-toaster-top-center',
      })
    },
  },
}
</script>

<style scoped>
.agronomic-page {
  padding: 28px;
  border: 1px solid #e3ebe7;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 45, 38, 0.07);
}

.agronomic-header,
.queue-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.agronomic-eyebrow {
  color: #008e75;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.agronomic-header h1 {
  margin: 3px 0 4px;
  color: #102a25;
  font-size: 28px;
  font-weight: 800;
}

.agronomic-header p,
.panel-subtitle {
  margin: 0;
  color: #64736f;
  font-size: 13px;
}

.agronomic-header-actions,
.queue-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin: 18px 0;
}

.status-card,
.ops-panel,
.queue-panel {
  border: 1px solid #dce8e3;
  border-radius: 10px;
  background: #fbfefd;
}

.status-card {
  padding: 12px;
}

.status-card strong {
  display: block;
  margin-top: 8px;
  color: #16352e;
  font-size: 22px;
}

.agronomic-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr 0.72fr;
  gap: 14px;
}

.ops-panel,
.queue-panel {
  padding: 16px;
}

.panel-title {
  color: #172b3a;
  font-size: 16px;
  font-weight: 800;
}

.switch-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 14px 0;
}

.soft-alert {
  border-radius: 8px;
  font-size: 12px;
}

.product-option {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.product-option-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.product-option span,
.selection-summary,
.result-box span {
  color: #64748b;
  font-size: 12px;
}

.inline-error,
.selected-error {
  color: #991b1b !important;
  font-weight: 700;
}

.product-detail-link {
  align-self: flex-start;
  padding: 0;
  color: #0f766e;
  font-size: 11px;
  font-weight: 700;
}

.selection-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-product-alerts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.selected-product-alert {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 9px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fff7f7;
}

.selected-product-alert div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.selected-product-alert strong {
  color: #172b3a;
  font-size: 12px;
}

.selected-product-alert span {
  color: #991b1b;
  font-size: 12px;
}

.run-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.result-box {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  border: 1px solid #d8e7e1;
  border-radius: 8px;
  background: #fff;
}

.run-results {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 170px;
  overflow: auto;
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px dashed #d8e7e1;
}

.run-result-item {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: #475569;
  font-size: 12px;
}

.run-result-item strong {
  color: #172b3a;
}

.queue-panel {
  margin-top: 14px;
}

.queue-table-wrap {
  margin-top: 14px;
  overflow: auto;
  border: 1px solid #dbe7e2;
  border-radius: 8px;
}

.queue-table {
  min-width: 1500px;
  margin-bottom: 0;
}

.queue-table th {
  position: sticky;
  top: 0;
  background: #f7fbf9;
  color: #263f37;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.queue-table td {
  color: #263b35;
  font-size: 12px;
  vertical-align: middle;
}

.queue-row-error td {
  background: #fff7f7;
}

.error-cell {
  max-width: 320px;
  white-space: normal;
}

.queue-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.queue-state,
.queue-empty {
  padding: 28px;
  color: #64748b;
  text-align: center;
}

.job-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.job-badge-total {
  color: #172b3a;
  background: #e2e8f0;
}

.job-badge-pending {
  color: #1e3a8a;
  background: #dbeafe;
}

.job-badge-processing {
  color: #92400e;
  background: #fef3c7;
}

.job-badge-success {
  color: #166534;
  background: #dcfce7;
}

.job-badge-error {
  color: #991b1b;
  background: #fee2e2;
}

.error-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-left: 6px;
  border-radius: 50%;
  background: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
}

.job-badge-muted {
  color: #475569;
  background: #e2e8f0;
}

.detail-modal-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px;
  border: 1px solid #dce8e3;
  border-radius: 8px;
  background: #fbfefd;
}

.detail-item span {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.detail-item strong {
  overflow-wrap: anywhere;
  color: #172b3a;
  font-size: 13px;
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-section-title {
  color: #172b3a;
  font-size: 14px;
  font-weight: 800;
}

.compact-state {
  padding: 14px;
  border: 1px dashed #d8e7e1;
  border-radius: 8px;
}

.logs-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.log-item {
  border: 1px solid #dce8e3;
  border-radius: 8px;
  background: #fbfefd;
}

.log-item-error {
  border-color: #fecaca;
  background: #fff7f7;
}

.log-item-success {
  border-color: #bbf7d0;
  background: #f7fff9;
}

.log-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px;
  color: #334155;
  font-size: 12px;
}

.log-main .btn {
  padding: 0;
  font-size: 12px;
  font-weight: 700;
}

.log-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 10px 10px;
}

.history-placeholder {
  padding: 10px;
  border: 1px dashed #d8e7e1;
  border-radius: 8px;
  color: #64748b;
  font-size: 12px;
}

.json-block {
  max-height: 320px;
  overflow: auto;
  margin: 0;
  padding: 12px;
  border: 1px solid #dce8e3;
  border-radius: 8px;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 12px;
  white-space: pre-wrap;
}

.error-json {
  border-color: #fecaca;
  background: #2a0f14;
  color: #fecaca;
}

.spinning {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .agronomic-grid,
  .status-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .agronomic-header,
  .queue-toolbar {
    flex-direction: column;
  }

  .agronomic-grid,
  .status-grid {
    grid-template-columns: 1fr;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .log-details {
    grid-template-columns: 1fr;
  }
}
</style>
